import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckIcon,
  XMarkIcon,
  RocketLaunchIcon,
  LockClosedIcon,
  Cog6ToothIcon,
  LifebuoyIcon,
  ShieldCheckIcon,
  UsersIcon,
  HandThumbUpIcon,
  CameraIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import ProEmailWizard from '../components/ProEmailWizard';
import { trackEvent } from '../lib/metaPixel';

const BUSINESS_WHATSAPP = '237653459638';

// Pricing is defined here (USD); copy/features come from the locale files.
const PACKS = [
  { id: 'starter', price: 19, oldPrice: 39, popular: false, featureCount: 6 },
  { id: 'business', price: 49, oldPrice: 99, popular: true, featureCount: 7 },
  { id: 'premium', price: 99, oldPrice: 199, popular: false, featureCount: 7 },
];

const ProEmail = () => {
  const { t } = useTranslation();
  const [activePack, setActivePack] = useState(null);

  useEffect(() => {
    trackEvent('ViewContent', { customData: { content_name: 'pro-email-landing' } });
  }, []);

  const perYear = t('proEmail.packs.perYear');

  const openWizard = (packDef) => {
    setActivePack({
      id: packDef.id,
      name: t(`proEmail.packs.${packDef.id}.name`),
      price: packDef.price,
      priceLabel: `$${packDef.price}${perYear}`,
    });
  };

  const scrollToPacks = () => {
    document.getElementById('packs')?.scrollIntoView({ behavior: 'smooth' });
  };

  const benefits = [
    { key: 'credibility', Icon: ShieldCheckIcon },
    { key: 'clients', Icon: UsersIcon },
    { key: 'respect', Icon: HandThumbUpIcon },
    { key: 'image', Icon: CameraIcon },
  ];

  const perks = [
    { key: 'rapid', Icon: RocketLaunchIcon },
    { key: 'secure', Icon: LockClosedIcon },
    { key: 'config', Icon: Cog6ToothIcon },
    { key: 'support', Icon: LifebuoyIcon },
  ];

  return (
    <div className="min-h-screen bg-white font-futura">
      <SEO
        path="/pro-email"
        title={t('proEmail.seo.title')}
        description={t('proEmail.seo.description')}
        image="/professional-email-address-csl-brands.jpg"
        keywords="professional email address, adresse email professionnelle, email hosting Cameroon, custom domain email, hébergement email, email pro entreprise, web design Cameroun, hosting Africa, nom de domaine"
        structuredData={{
          '@type': 'Product',
          name: 'Professional Email — CSL Brands',
          description: t('proEmail.seo.description'),
          brand: { '@type': 'Brand', name: 'CSL Brands' },
          offers: PACKS.map((p) => ({
            '@type': 'Offer',
            name: t(`proEmail.packs.${p.id}.name`),
            price: p.price,
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
          })),
        }}
      />

      {/* ── Hero ── */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-csl-dark-green">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-csl-green/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-16 w-64 h-64 bg-white/5 rounded-full blur-2xl pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-csl-green/20 text-csl-light-green text-sm font-semibold mb-6">
            <EnvelopeIcon className="w-4 h-4" />
            {t('proEmail.hero.badge')}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight">
            {t('proEmail.hero.titleLine1')}{' '}
            <span className="text-csl-light-green">{t('proEmail.hero.titleHighlight')}</span>
          </h1>
          <p className="max-w-2xl mx-auto text-gray-300 text-base sm:text-lg leading-relaxed mb-8">
            {t('proEmail.hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={scrollToPacks}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-csl-green text-white font-semibold rounded-xl hover:bg-csl-light-green transition-colors"
            >
              {t('proEmail.hero.ctaPrimary')}
              <ArrowRightIcon className="w-5 h-5" />
            </button>
            <a
              href={`https://wa.me/${BUSINESS_WHATSAPP}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors border border-white/20"
            >
              <ChatBubbleLeftRightIcon className="w-5 h-5" />
              {t('proEmail.hero.ctaSecondary')}
            </a>
          </div>
        </div>
      </section>

      {/* ── Problem: non-pro vs pro ── */}
      <section className="py-14 sm:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Non-professional */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-7">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 text-red-600 text-sm font-bold mb-5">
                <XMarkIcon className="w-4 h-4" />
                {t('proEmail.problem.nonProTitle')}
              </div>
              <ul className="space-y-3">
                {[t('proEmail.problem.nonProExample1'), t('proEmail.problem.nonProExample2')].map((ex) => (
                  <li key={ex} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-100 text-gray-500 line-through">
                    <span className="w-6 h-6 rounded-full bg-red-100 text-red-500 flex items-center justify-center flex-shrink-0">
                      <XMarkIcon className="w-4 h-4" />
                    </span>
                    <span className="text-sm font-medium break-all">{ex}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Professional */}
            <div className="bg-white rounded-2xl border-2 border-csl-green/30 shadow-md p-6 sm:p-7">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-csl-green/10 text-csl-green text-sm font-bold mb-5">
                <CheckIcon className="w-4 h-4" />
                {t('proEmail.problem.proTitle')}
              </div>
              <ul className="space-y-3">
                {[t('proEmail.problem.proExample1'), t('proEmail.problem.proExample2')].map((ex) => (
                  <li key={ex} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-csl-green/5 text-gray-800">
                    <span className="w-6 h-6 rounded-full bg-csl-green text-white flex items-center justify-center flex-shrink-0">
                      <CheckIcon className="w-4 h-4" />
                    </span>
                    <span className="text-sm font-semibold break-all">{ex}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Benefits */}
          <div className="mt-10 text-center">
            <h2 className="text-lg sm:text-xl font-bold text-csl-dark-green uppercase tracking-wide mb-6">
              {t('proEmail.problem.equals')}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {benefits.map(({ key, Icon }) => (
                <div
                  key={key}
                  className="flex flex-col items-center gap-3 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm"
                >
                  <span className="w-12 h-12 rounded-xl bg-csl-green/10 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-csl-green" />
                  </span>
                  <span className="text-sm font-semibold text-gray-800 text-center">
                    {t(`proEmail.problem.benefits.${key}`)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Perks ── */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-l-4 border-csl-green pl-4 mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('proEmail.perks.title')}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {perks.map(({ key, Icon }) => (
              <div key={key} className="flex items-center gap-3 p-4 rounded-2xl bg-gray-50 border border-gray-100">
                <span className="w-11 h-11 rounded-xl bg-csl-green/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-csl-green" />
                </span>
                <span className="text-sm font-semibold text-gray-800">{t(`proEmail.perks.${key}`)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing packs ── */}
      <section id="packs" className="py-14 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              {t('proEmail.packs.title')}
            </h2>
            <p className="text-gray-600 text-sm sm:text-base">{t('proEmail.packs.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {PACKS.map((pack) => {
              const features = t(`proEmail.packs.${pack.id}.features`, { returnObjects: true }) || [];
              const savePct = Math.round(((pack.oldPrice - pack.price) / pack.oldPrice) * 100);
              return (
                <div
                  key={pack.id}
                  className={`relative flex flex-col rounded-3xl bg-white p-6 sm:p-7 transition-all ${
                    pack.popular
                      ? 'border-2 border-csl-green shadow-2xl md:-translate-y-3'
                      : 'border border-gray-100 shadow-sm hover:shadow-md'
                  }`}
                >
                  {pack.popular && (
                    <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-csl-green text-white text-xs font-bold uppercase tracking-wider shadow-lg whitespace-nowrap">
                      {t('proEmail.packs.popular')}
                    </span>
                  )}

                  <h3 className="text-lg font-bold text-gray-900">{t(`proEmail.packs.${pack.id}.name`)}</h3>
                  <p className="text-sm text-gray-500 mt-1 min-h-[40px]">{t(`proEmail.packs.${pack.id}.tagline`)}</p>

                  <div className="mt-5 flex items-end gap-2">
                    <span className="text-4xl font-extrabold text-gray-900">${pack.price}</span>
                    <span className="text-gray-500 text-sm mb-1">{perYear}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-gray-400 line-through">${pack.oldPrice}</span>
                    <span className="text-xs font-bold text-csl-green bg-csl-green/10 px-2 py-0.5 rounded-full">
                      -{savePct}%
                    </span>
                    <span className="text-xs text-gray-400">{t('proEmail.packs.billedYearly')}</span>
                  </div>

                  <button
                    onClick={() => openWizard(pack)}
                    className={`mt-6 w-full py-3 rounded-xl font-semibold text-sm transition-colors ${
                      pack.popular
                        ? 'bg-csl-green text-white hover:bg-csl-dark-green'
                        : 'bg-gray-900 text-white hover:bg-csl-green'
                    }`}
                  >
                    {t('proEmail.packs.choose')}
                  </button>

                  <ul className="mt-6 space-y-3">
                    {features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-csl-green/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckIcon className="w-3.5 h-3.5 text-csl-green" />
                        </span>
                        <span className="text-sm text-gray-700">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-14 bg-gradient-to-r from-csl-green to-csl-dark-green">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            {t('proEmail.perks.title')}
          </h2>
          <p className="text-white/80 mb-7 text-sm sm:text-base">{t('proEmail.packs.subtitle')}</p>
          <button
            onClick={scrollToPacks}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-csl-dark-green font-bold rounded-xl hover:bg-gray-100 transition-colors"
          >
            {t('proEmail.hero.ctaPrimary')}
            <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>
      </section>

      {activePack && <ProEmailWizard pack={activePack} onClose={() => setActivePack(null)} />}
    </div>
  );
};

export default ProEmail;
